import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import type { PickRef, PickRefBundle } from "@/lib/brand-site-pick-refs";
import type { PickRefComposerContent } from "@/lib/pick-ref-mixed-editor";
import { serializePickRefComposerForClipboard } from "@/lib/pick-ref-mixed-editor";
import { cn } from "@/lib/utils";
import {
  PickRefMixedEditor,
  type PickRefMixedEditorHandle,
} from "@/components/brand-site/PickRefMixedEditor";

export type PickRefChipInputHandle = {
  getContent: () => PickRefComposerContent | null;
  focus: () => void;
};

type PickRefChipInputProps = {
  bundle: PickRefBundle;
  onPicksChange?: (picks: PickRef[]) => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  submitting?: boolean;
  showActions?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
};

/** 点选改页输入：@n 芯片与文字混排，可复制粘贴到右侧 Hermes。 */
export const PickRefChipInput = forwardRef<PickRefChipInputHandle, PickRefChipInputProps>(
  function PickRefChipInput(
    {
      bundle,
      onPicksChange,
      onSubmit,
      onCancel,
      submitting = false,
      showActions = true,
      autoFocus = false,
      placeholder = "描述要如何改这些选区…",
      className,
    },
    ref,
  ) {
    const editorRef = useRef<PickRefMixedEditorHandle>(null);
    const insertedPickIdsRef = useRef<Set<string>>(new Set());

    useImperativeHandle(
      ref,
      () => ({
        getContent: () => editorRef.current?.getContent() ?? null,
        focus: () => editorRef.current?.focus(),
      }),
      [],
    );

    useEffect(() => {
      insertedPickIdsRef.current = new Set();
      editorRef.current?.clear();
      for (const pick of bundle.picks) {
        editorRef.current?.insertPick(pick);
        insertedPickIdsRef.current.add(pick.id);
      }
    }, [bundle.pageSlug, bundle.pagePath]);

    useEffect(() => {
      for (const pick of bundle.picks) {
        if (insertedPickIdsRef.current.has(pick.id)) continue;
        editorRef.current?.insertPick(pick);
        insertedPickIdsRef.current.add(pick.id);
      }
    }, [bundle.picks]);

    function handleChange(content: PickRefComposerContent | null) {
      if (!content) return;
      onPicksChange?.(content.picks);
    }

    useEffect(() => {
      function onCopy(event: ClipboardEvent) {
        const content = editorRef.current?.getContent();
        if (!content || content.picks.length === 0 || !event.clipboardData) return;
        const { text, html } = serializePickRefComposerForClipboard(content);
        event.preventDefault();
        event.clipboardData.setData("text/plain", text);
        event.clipboardData.setData("text/html", html);
      }
      document.addEventListener("copy", onCopy);
      return () => document.removeEventListener("copy", onCopy);
    }, []);

    return (
      <div
        className={cn(
          "overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg ring-1 ring-black/5 dark:ring-white/10",
          className,
        )}
      >
        <div className="p-2.5">
          <PickRefMixedEditor
            ref={editorRef}
            pageContext={{
              pageSlug: bundle.pageSlug,
              pageTitle: bundle.pageTitle,
              pagePath: bundle.pagePath,
            }}
            placeholder={bundle.picks.length === 0 ? "先点选页面元素…" : placeholder}
            autoFocus={autoFocus}
            onChange={handleChange}
            onSubmit={onSubmit}
          />
        </div>

        {showActions ? (
          <div className="flex items-center justify-end gap-1.5 border-t border-border px-2.5 py-2">
            {onCancel ? (
              <button
                type="button"
                onClick={onCancel}
                className="rounded-md px-2 py-1 text-xs text-foreground/75 hover:bg-muted hover:text-foreground"
              >
                取消
              </button>
            ) : null}
            {onSubmit ? (
              <button
                type="button"
                disabled={bundle.picks.length === 0 || submitting}
                onClick={onSubmit}
                className="rounded-md bg-primary px-2.5 py-1 text-xs text-primary-foreground disabled:opacity-40"
              >
                {submitting ? "处理中…" : "发送"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  },
);
